import { cva } from 'class-variance-authority';

/**
 * Checkbox class recipes.
 *
 * Like `Button.recipe` and `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-checkbox-*` slots that `.ds-checkbox` sets in
 * `components/styles/design-system.css`. As of the 2026-09-15 rework those slots are semantic roles
 * that invert by themselves, so that block has no dark rule at all any more.
 *
 * ── The geometry, and why it is what it is ─────────────────────────────────────────────────────
 *
 * All 12 cells of Figma node 585:57 share one shape, measured off the nodes rather than eyeballed:
 *
 *   a 24x24 `Input` frame           the hit target
 *   an 18x18 rect at (3, 3)         with a 1px INSIDE stroke
 *   `radius/sm` on that rect        6px
 *
 * An INSIDE stroke is what makes this translate with no arithmetic: the app is `box-sizing:
 * border-box`, so an 18px element inset 3px with a 1px border is the same pixels Figma draws. The
 * previous port had to convert a CENTRED 1.5px stroke; the rework moved to 1px inside, which is both
 * thinner and simpler.
 *
 * ── Nothing moves, in any state ────────────────────────────────────────────────────────────────
 *
 * The border is 1px in every cell, so there is no stroke to thicken. The focus treatment is a
 * BOX-SHADOW, which occupies no layout space, so focus costs nothing either — `NothingMovesOnFocus`
 * in the stories measures both claims.
 *
 * ── The focus ring ─────────────────────────────────────────────────────────────────────────────
 *
 * The rework replaced the old blue halo with the design system's SHARED ring, and this is the first
 * component in the repo to ship it (`Button.recipe` deferred it deliberately). Figma draws it as two
 * zero-blur drop shadows on the 18px rect:
 *
 *   spread 2  `effect/focus-inner`   the page colour — a gap between box and halo
 *   spread 4  `effect/focus-outer`   the foreground at 40% — the halo itself
 *
 * Composed here exactly as `styles/FocusRing.stories` documents it, every part named: eleven custom
 * properties and not one length or colour literal. Inner is listed FIRST so it paints on top of the
 * wider outer one.
 *
 * Both rings are low-contrast tints by construction — 2.64:1 light, 3.81:1 dark composited — so
 * neither is the indicator on its own. What carries WCAG 1.4.11 is the pair plus the box's own
 * border, which is why the ring is drawn on the box rather than on the 24px parent: at 4px spread it
 * already overflows the hit area, and hanging it off the parent would put a 2px gap in the wrong
 * place.
 *
 * `focus-visible` rather than `focus`, so the ring answers a keyboard tab and not a mouse click.
 * Figma's Focus cell does not say which it means; showing it on click would ring every box the user
 * merely toggles.
 *
 * ── How the states are selected ────────────────────────────────────────────────────────────────
 *
 * `group/cb` sits on `Checkbox.Root`, which is where Base UI puts `data-checked`, `data-unchecked`,
 * `data-indeterminate` and `data-disabled`, and which is also the focusable element. So one group
 * serves the whole component and there is no React state behind any colour change.
 *
 * The pivot is `data-unchecked`, which Base UI sets ONLY when the box is neither ticked nor
 * indeterminate. `:not([data-unchecked])` therefore means "showing a glyph" in one selector instead
 * of matching `data-checked` and `data-indeterminate` separately.
 * `IndeterminateHasNeitherCheckedNorUnchecked` in the tests guards that premise, since the whole
 * recipe rests on it.
 *
 * HOVER IS NOT IN THAT GROUP, and that is the one structural change worth reading before editing.
 * It is handled on the root `<label>` by REMAPPING the resting slots to their hover values, so the
 * rules below never mention hover at all. Two things fall out of that:
 *
 *   - the whole row hovers, not just the 24px box. The `<label>` wraps both, a click anywhere in it
 *     toggles, and Figma's Hover cell moves the label colour as well as the box — so treating the
 *     text as part of the hover target is what the drawing says.
 *   - the eight selectors the old recipe needed to keep hover and focus from fighting are gone. The
 *     slot indirection does the work, exactly as `Button.recipe` uses it for `loading`.
 *
 * What is left is mutually exclusive on `data-*` alone, so nothing here depends on Tailwind's
 * utility sort order:
 *
 *   unchecked (base) | unchecked+focus | unchecked+disabled | on | on+disabled
 */

/**
 * The wrapper, which is a real `<label>`.
 *
 * `cursor` is set here and inherited by everything inside, so the box and the text agree — a
 * checkbox whose label is a text cursor and whose box is a pointer looks broken.
 *
 * `items-start`, not `items-center`: a label long enough to wrap must leave the box on the first
 * line rather than dragging it down to the centre of the whole text block. Figma's own frame is
 * `CENTER`, which is the same thing for the single line it draws — see the note on
 * `checkboxLabelRecipe` for the 2px that keeps that case pixel-identical.
 *
 * `gap-2` is Figma's `x2` (8px) and `py-0.5` its `x0,5` (2px), both off the Spacing collection.
 */
export const checkboxRootRecipe = cva(['ds-checkbox', 'inline-flex w-fit items-start gap-2 py-0.5'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed',
      /**
       * Hover, as a slot remap rather than as a state selector — see the header.
       *
       * Each line points a RESTING slot at its own hover value, which every rule below already
       * reads. So the box and the label pick hover up for free, the disabled row cannot reach it
       * (these classes only exist on the enabled variant), and there is no selector anywhere that
       * has to out-specify focus to get out of its way.
       */
      false: [
        'cursor-pointer',
        'hover:[--ds-checkbox-bg:var(--ds-checkbox-bg-hover)]',
        'hover:[--ds-checkbox-border:var(--ds-checkbox-border-hover)]',
        'hover:[--ds-checkbox-on-bg:var(--ds-checkbox-on-bg-hover)]',
        'hover:[--ds-checkbox-on-border:var(--ds-checkbox-on-border-hover)]',
        'hover:[--ds-checkbox-label:var(--ds-checkbox-label-hover)]',
      ],
    },
  },
  defaultVariants: { disabled: false },
});

/**
 * `Checkbox.Root` — the 24px hit area and the group every other recipe reads.
 *
 * It holds no colour of its own any more. The old blue halo lived here and needed a radius of its
 * own; the shared ring is a shadow on the box, so this is now purely the hit target.
 *
 * `outline-none` replaces the UA focus ring, which the composed ring stands in for.
 */
export const checkboxControlRecipe = cva([
  'group/cb',
  'relative block size-6 shrink-0 cursor-[inherit]',
  'bg-transparent outline-none',
]);

/**
 * The box itself — a child of the control rather than the control, because the ring's 4px outer
 * spread reaches 1px beyond the 24px hit area and the 2px gap has to sit against the box's edge.
 *
 * `pointer-events-none` so it can never intercept a click that belongs to the root above it.
 */
export const checkboxBoxRecipe = cva([
  'pointer-events-none absolute inset-[3px] rounded-sm',
  'border border-solid',
  'transition-[border-color,background-color,box-shadow] duration-150 motion-reduce:transition-none',

  /**
   * The ring, drawn unconditionally with both colours defaulting to transparent, so focus flips two
   * custom properties instead of switching a whole `box-shadow` on and off. `TextInput.recipe` uses
   * the same shape for its stroke pair, and the reason is the same: one declaration means the
   * transition has something to interpolate and there is no second rule to keep in step.
   */
  '[--ds-checkbox-ring-inner:transparent] [--ds-checkbox-ring-outer:transparent]',
  'shadow-[var(--effect-focus-inner-x)_var(--effect-focus-inner-y)_var(--effect-focus-inner-blur)_var(--effect-focus-inner-spread)_var(--ds-checkbox-ring-inner),var(--effect-focus-outer-x)_var(--effect-focus-outer-y)_var(--effect-focus-outer-blur)_var(--effect-focus-outer-spread)_var(--ds-checkbox-ring-outer)]',
  'group-[:not([data-disabled]):focus-visible]/cb:[--ds-checkbox-ring-inner:var(--effect-focus-inner)]',
  'group-[:not([data-disabled]):focus-visible]/cb:[--ds-checkbox-ring-outer:var(--effect-focus-outer)]',

  // Unchecked, resting. The base case, so it needs no selector — everything below out-specifies it.
  'border-[var(--ds-checkbox-border)] bg-[var(--ds-checkbox-bg)]',

  /**
   * Unchecked, focused. The stroke keeps its resting colour — the rework does NOT turn it blue, so
   * the ring is the whole of the focus treatment here. What changes is the FILL, to the ring's own
   * inner colour, which makes the box read as a hole in the page rather than as a filled chip
   * wearing a halo.
   */
  'group-[[data-unchecked]:not([data-disabled]):focus-visible]/cb:bg-[var(--ds-checkbox-bg-focus)]',

  // Unchecked, disabled.
  'group-[[data-unchecked][data-disabled]]/cb:border-[var(--ds-checkbox-border-disabled)]',
  'group-[[data-unchecked][data-disabled]]/cb:bg-[var(--ds-checkbox-bg-disabled)]',

  /**
   * Ticked or indeterminate. Fill and stroke are now DIFFERENT values — Blue/600 inside Blue/700 in
   * light mode — where the previous port had one slot for both. Focus adds the ring and changes
   * nothing else on these cells, which is why there is no focused variant of this pair.
   */
  'group-[:not([data-unchecked]):not([data-disabled])]/cb:border-[var(--ds-checkbox-on-border)]',
  'group-[:not([data-unchecked]):not([data-disabled])]/cb:bg-[var(--ds-checkbox-on-bg)]',

  // Ticked or indeterminate, disabled — a neutral box, not a washed-out blue one.
  'group-[:not([data-unchecked])[data-disabled]]/cb:border-[var(--ds-checkbox-on-border-disabled)]',
  'group-[:not([data-unchecked])[data-disabled]]/cb:bg-[var(--ds-checkbox-on-bg-disabled)]',
]);

/**
 * `Checkbox.Indicator` — the glyph layer, mounted only while there is a glyph to draw.
 *
 * Sized to the full 24px rather than to the 18px box, so the paths inside can be Figma's own
 * coordinates in Figma's own frame with no arithmetic in between.
 */
export const checkboxGlyphRecipe = cva([
  'pointer-events-none absolute inset-0 block',
  'text-[var(--ds-checkbox-glyph)]',
  'group-data-[disabled]/cb:text-[var(--ds-checkbox-glyph-disabled)]',
  '[&_svg]:size-full',
]);

/**
 * The label text.
 *
 * `py-0.5` is what makes `items-start` on the wrapper safe. The text is 14px on a 20px line and the
 * box is 24px, so top-aligning the two raw would sit the first line 2px above the box's optical
 * centre. 2px of padding grows the FIRST LINE BOX to 24px, which centres it against the box and
 * leaves a single-line checkbox rendering identically to Figma's `CENTER` frame — the only thing
 * that changes is where the box sits once the text wraps. `LongLabel` measures both halves of that
 * claim.
 *
 * Both colours are variants rather than a base plus an override: two bare `text-*` utilities have
 * the same specificity, so which one won would come down to Tailwind's output order rather than
 * anything written here. Hover is not a third variant — `--ds-checkbox-label` is remapped on the
 * wrapper, so this reads one slot and gets both values.
 */
export const checkboxLabelRecipe = cva(['font-sans text-body-md select-none py-0.5'], {
  variants: {
    disabled: {
      true: 'text-[var(--ds-checkbox-label-disabled)]',
      false: 'text-[var(--ds-checkbox-label)]',
    },
  },
  defaultVariants: { disabled: false },
});
